/**
 * Browser-side record of the Gemini proxy's rate-limit window.
 *
 * `/api/gemini-proxy` meters requests per client IP in the shared
 * `generalRateLimit` bucket — 100 requests per 60 s fixed window
 * (`src/server/ipRateLimit.ts`), shared with every other route that uses it —
 * and answers an over-budget request with `429` + `Retry-After: 60`. The
 * @google/genai SDK turns that response into an `ApiError` that carries only
 * the status and the JSON body; the header is lost. The one place that can
 * read it is the browser fetch patch in `services/geminiClient.ts`, which
 * records it here. A caller that fans out many proxied calls (DM2 doc
 * matching: `services/docMatching/scoringThrottle.ts`) reads
 * `geminiProxyBlockedUntil()` and holds new requests until the window has
 * passed, instead of firing into a closed bucket.
 *
 * Module state is per tab. A server runtime records nothing: server calls go
 * to Vertex directly, not through the proxy.
 */
import { parseRetryAfterMs } from './httpRetry';

/**
 * Wait assumed for a 429 without a usable `Retry-After`: the value the proxy
 * sends with every 429 (`server.ts`), one `IP_RATE_LIMIT_WINDOW_MS`.
 */
export const GEMINI_PROXY_DEFAULT_RETRY_AFTER_MS = 60_000;

/**
 * Ceiling on an honoured `Retry-After`. The proxy sends 60 s; a larger value
 * would come from something else on the path, and must not park the tab's
 * doc matching for longer than this.
 */
export const GEMINI_PROXY_MAX_RETRY_AFTER_MS = 5 * 60_000;

let blockedUntilMs = 0;

/**
 * Record a 429 from the proxy and return the epoch ms before which no new
 * proxied request should start.
 *
 * `retryAfter` is the raw header (seconds or an HTTP date). Without a usable
 * value the default 60 s applies — unless a window is already open: the fetch
 * patch records the header first, and a caller that catches the resulting
 * SDK error reports the same 429 again without it, which must not stretch the
 * server's window. A later window never shortens an open one.
 */
export function noteGeminiProxyRateLimited(
  retryAfter?: string | null,
  now: number = Date.now(),
): number {
  const parsed = parseRetryAfterMs(retryAfter);
  if (parsed === null && blockedUntilMs > now) return blockedUntilMs;
  const waitMs = Math.min(parsed ?? GEMINI_PROXY_DEFAULT_RETRY_AFTER_MS, GEMINI_PROXY_MAX_RETRY_AFTER_MS);
  blockedUntilMs = Math.max(blockedUntilMs, now + waitMs);
  return blockedUntilMs;
}

/** Epoch ms before which no new proxied request should start (0 = never blocked). */
export function geminiProxyBlockedUntil(): number {
  return blockedUntilMs;
}

/** Test hook — forget any recorded window. */
export function resetGeminiProxyRateLimitForTests(): void {
  blockedUntilMs = 0;
}
